/**
 * Empty state for the items list.
 *
 * Mirrors `apps/web/src/features/customers/components/empty-state.tsx`:
 * first-run shows the New-item CTA once, tab/filter empties show honest
 * "no data available" copy without a misleading primary action.
 */
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { Card, CardBody } from "@flint/ui";

export type ItemsTabId = "all" | "goods" | "services" | "low_stock" | "untagged" | "archived";

export interface EmptyStateProps {
  variant: "first-run" | "no-archived" | "no-search-match" | "no-filter-match" | "no-tab-match";
  query?: string;
  tab?: ItemsTabId;
  /** Only relevant on `first-run` — hides the CTA for users without `items.manage`. */
  canManage?: boolean;
  /** `no-filter-match` — parent hands a callback so the button can clear filters. */
  onClearFilters?: () => void;
}

const TAB_EMPTY_COPY: Record<
  Exclude<ItemsTabId, "all" | "archived">,
  { title: string; hint: string }
> = {
  goods: {
    title: "No goods on the catalogue yet.",
    hint: "Physical items you sell or buy appear here as soon as they're added.",
  },
  services: {
    title: "No services on the catalogue yet.",
    hint: "Labour, jobs, and other non-physical items show up on this tab.",
  },
  low_stock: {
    title: "No items are low on stock.",
    hint: "Tracked items fall onto this tab once their balance drops to the reorder level.",
  },
  untagged: {
    title: "Every item has at least one tag.",
    hint: "Untagged items surface here so you can categorise them before an audit.",
  },
};

export function EmptyState({
  variant,
  query,
  tab,
  canManage = true,
  onClearFilters,
}: EmptyStateProps) {
  if (variant === "no-search-match") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">
            No items match {query ? <>“{query}”</> : "this search"}.
          </p>
          <p className="text-sm text-muted mt-1">
            Try a shorter phrase, or search by SKU.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (variant === "no-filter-match") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">No data available.</p>
          <p className="text-sm text-muted mt-1">
            No catalogue rows match the current filters.
          </p>
          {onClearFilters ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="btn-secondary inline-flex items-center gap-1.5 mt-4 h-9 px-3.5 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2"
            >
              Clear filters
            </button>
          ) : null}
        </CardBody>
      </Card>
    );
  }

  if (variant === "no-tab-match" && tab && tab !== "all" && tab !== "archived") {
    const copy = TAB_EMPTY_COPY[tab];
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">{copy.title}</p>
          <p className="text-sm text-muted mt-1">{copy.hint}</p>
        </CardBody>
      </Card>
    );
  }

  if (variant === "no-archived") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">No archived items.</p>
          <p className="text-sm text-muted mt-1">
            Archived rows show up here once you retire one from the catalogue.
          </p>
        </CardBody>
      </Card>
    );
  }

  // first-run — the whole catalogue is empty.
  return (
    <Card>
      <CardBody className="text-center py-10">
        <p className="text-base font-medium">No items yet.</p>
        <p className="text-sm text-muted mt-1 max-w-md mx-auto">
          Add your first catalogue entry to start using the line picker on invoices and bills.
        </p>
        {canManage ? (
          <Link
            to="/sales/items/new"
            className="inline-flex items-center justify-center gap-1.5 mt-4 h-9 px-4 rounded-sm bg-ink text-paper text-sm font-medium hover:bg-ink-2"
          >
            <Plus size={13} strokeWidth={2.4} aria-hidden />
            Add your first item
          </Link>
        ) : null}
      </CardBody>
    </Card>
  );
}
